import { Transition } from '@headlessui/react';
import { useState, useEffect } from 'react';
import LoadingSpinner from '../LoadingSpinner';
import Image from '../Image';

const tips = [
  {
    title: 'Tip:',
    label:
      'Use the "Books" feature to get resources aligned to the novels and chapter books you\'re reading with your class!',
  },
  {
    title: 'Tip:',
    label:
      'Customize your activities the way YOU like them - mix and match sections, remove or re-order pages, and more!',
  },
  {
    title: 'Tip:',
    label: 'Re-order pages by dragging them up and down in the Table of Contents!',
  },
  {
    title: 'Tip:',
    label:
      'Use the "Key Vocabulary with Images" activity to use images with your vocabulary words.',
  },
  {
    title: 'Tip:',
    label:
      'Click "Preserve Original Text" when working with your PDFs, texts, or articles to get differentiated resources while keeping your inputs unchanged',
  },
  {
    title: 'Tip:',
    label:
      'Be as specific as possible in the "Literally Anything" search bar for best results, or click to align to standards.',
  },
  {
    title: 'Tip:',
    label:
      "Use the Translate button above your resource to adapt content into your students' native language.",
  },
  {
    title: 'Tip:',
    label: "Use Diffit to align intervention materials with what's being covered in class.",
  },
  {
    title: 'Tip:',
    label:
      'Use Diffit to build background knowledge so students are better prepared for readings or class discussion.',
  },
  {
    title: 'Tip:',
    label:
      "Check results. We're using the best-available AI and improving all the time, but mistakes can still happen - especially with niche topics.",
  },
  {
    title: 'Tip:',
    label: 'Get more questions at different DOK levels by clicking "Add Questions"!',
  },
  {
    title: 'Tip:',
    label: 'Choose your own vocabulary word by clicking "Add Vocabulary"',
  },
  {
    title: 'Tip:',
    label:
      'Modify readings, questions and vocabulary using the special tools featured above each page',
  },
  {
    title: 'Tip:',
    label: 'You can share your Google Slides, Docs, and Forms directly to Google Classroom.',
  },
  {
    title: 'Tip:',
    label:
      'You can adapt longer texts (up to ~10,000 words) using the article URL, PDF, or Text/Excerpt inputs.',
  },
  {
    title: 'Tip:',
    label:
      'Use "My Resources" to see all the Diffit resources you\'ve made - and share them with colleagues!',
  },
  {
    title: 'Tip:',
    label:
      'Switch images using the image editor, or upload your own image to replace what you see!',
  },
  {
    title: 'Tip:',
    label: 'See citations by clicking into the reading passage and selecting "Show Sources" ✅',
  },
  {
    title: 'Tip:',
    label: 'Print, download PDF, or get student-ready exports in Google Docs, Microsoft, and more!',
  },
  {
    title: 'Tip:',
    label:
      'You can align your content to standards, DOK levels and literacy skills in a few clicks.',
    src: '/tip-standards-srr.png',
  },
];

export default function LoadingScreen({ customMessage = null }) {
  const initialIndex = Math.floor(Math.random() * tips.length);

  const [tipIndex, setTipIndex] = useState(initialIndex);
  const [usedTips, setUsedTips] = useState([initialIndex]);
  const [loadingProgress, setLoadingProgress] = useState(5);
  const [isShowing, setIsShowing] = useState(true);

  function delay(t, v) {
    return new Promise((resolve) => setTimeout(resolve, t, v));
  }

  function randomize(list, index, setIndex, used, setUsed) {
    let newUsed = used;
    if (used.length === list.length) {
      newUsed = [index];
    }
    let randomIndex;
    do {
      randomIndex = Math.floor(Math.random() * list.length);
    } while (randomIndex === index || newUsed.includes(randomIndex));
    setIndex(randomIndex);
    setUsed([...newUsed, randomIndex]);
  }

  useEffect(() => {
    delay(7600).then(() => setIsShowing(false));

    const tipInterval = setInterval(() => {
      // Rotate tips in a random order without repeating
      randomize(tips, tipIndex, setTipIndex, usedTips, setUsedTips);

      setIsShowing(true);
      delay(7600).then(() => setIsShowing(false));
    }, 8000);

    // Increment loading progress randomly over ~17 seconds
    const loadingInterval = setInterval(() => {
      setLoadingProgress((progress) => {
        const newProgress = progress + Math.random() * 10.5;
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, 1000);

    // Clear intervals on component unmount
    return () => {
      clearInterval(tipInterval);
      clearInterval(loadingInterval);
    };
  }, [tipIndex]);

  return (
    <div className='fixed h-full w-full inset-0 bg-white/80 backdrop-blur-sm z-[70]'>
      <div className='flex flex-col gap-6 h-screen items-center mt-10 sm:mt-24 mx-4'>
        <div className='flex w-full h-96 p-4 max-w-2xl justify-center items-center text-center '>
          {customMessage ? (
            <p className='text-2xl text-gray-700 font-medium'>{customMessage}</p>
          ) : (
            <Transition
              appear={true}
              show={isShowing}
              enter='transition-opacity duration-200'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='transition-opacity duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'>
              {tips[tipIndex].src && (
                <Image
                  src={tips[tipIndex].src}
                  className='w-full mb-4 drop-shadow-lg'
                  alt={tips[tipIndex].label}
                />
              )}
              <p className='text-xl text-gray-600'>
                {!tips[tipIndex].src && <strong>{tips[tipIndex].title} </strong>}
                {tips[tipIndex].label}
              </p>
            </Transition>
          )}
        </div>
        <div className='inline-flex gap-2 w-11/12 max-w-2xl h-4 -mt-4 -mr-8 sm:-mr-14'>
          <div className='w-11/12 bg-gray-200 rounded-full'>
            <div
              className='bg-emerald-500 h-4 rounded-full transition-all duration-1000'
              style={{ width: `${loadingProgress}%` }}></div>
          </div>
          {loadingProgress === 100 && <LoadingSpinner />}
        </div>
      </div>
    </div>
  );
}
